/**
 * f12.1: the "Review attributes" modal of `review.html` -- risk segmented
 * control, Upside, Downside, "Comments <closing month>" and the read-only
 * history below it.
 *
 * Fields the caller may not write are disabled from the row's server-computed
 * `capabilities`; the PATCH re-checks everything. Only changed fields are
 * sent, so a comment-only caller never posts risk. A `409` reloads the current
 * values into the form with "Someone else changed this row".
 */

import { useState } from 'react';
import { Check, Info } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { ReviewRow, ReviewRowChange } from '@/api/client';
import { Modal } from '@/routes/reference-data/Modal';

import { CommentThread } from './CommentThread';
import { amountInput } from './reviewFormat';
import { CYCLE_NOT_EDITABLE, conflictRow, useReviewComments, useSaveReviewRow } from './useReview';

const RISKS = ['H', 'M', 'L'] as const;

interface FormState {
  risk: string;
  upside: string;
  downside: string;
  comment: string;
}

function formOf(row: ReviewRow): FormState {
  return {
    risk: row.risk_rating?.code ?? '',
    upside: row.upside?.amount ?? '',
    downside: row.downside?.amount ?? '',
    comment: row.comment ?? '',
  };
}

function changesOf(row: ReviewRow, form: FormState): Omit<ReviewRowChange, 'expected_version'> {
  const initial = formOf(row);
  const change: Omit<ReviewRowChange, 'expected_version'> = {};
  if (row.capabilities.edit_attributes) {
    if (form.risk !== initial.risk) change.risk_rating_code = (form.risk || null) as ReviewRowChange['risk_rating_code'];
    if (form.upside.trim() !== initial.upside) change.upside = amountInput(form.upside);
    if (form.downside.trim() !== initial.downside) change.downside = amountInput(form.downside);
  }
  if (row.capabilities.write_comment && form.comment !== initial.comment) {
    change.comment = form.comment.trim() ? form.comment : null;
  }
  return change;
}

export function ReviewAttributesDialog({
  cycleId,
  cycleCode,
  closingMonthName,
  row,
  latest,
  onClose,
}: {
  cycleId: string;
  cycleCode: string;
  closingMonthName: string;
  row: ReviewRow;
  /** f12.2: the row as the live cache holds it now; never copied into the form. */
  latest?: ReviewRow;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState<ReviewRow>(row);
  // f12.2: a push may deliver a newer row while the modal is open. The typed
  // values and the version the modal opened with are kept, so Save answers 409
  // and reloads (f12.1) instead of silently overwriting someone else's change.
  const changedElsewhere = latest !== undefined && (latest.version ?? 0) > (current.version ?? 0);
  const [form, setForm] = useState<FormState>(() => formOf(row));
  const [message, setMessage] = useState<string | null>(null);
  const save = useSaveReviewRow(cycleId);
  const thread = useReviewComments(cycleId, current);

  const canAttributes = current.capabilities.edit_attributes;
  const canComment = current.capabilities.write_comment;
  const readOnly = !canAttributes && !canComment;
  const change = changesOf(current, form);
  const dirty = Object.keys(change).length > 0;
  const fieldError = (key: string) =>
    save.error instanceof ApiError ? save.error.errors.find((e) => e.key === key)?.message : undefined;

  const submit = () => {
    if (readOnly || !dirty) {
      onClose();
      return;
    }
    setMessage(null);
    save.mutate(
      { row: current, change },
      {
        onSuccess: () => onClose(),
        onError: (error) => {
          const fresh = conflictRow(error);
          if (fresh) {
            setCurrent(fresh);
            setForm(formOf(fresh));
            setMessage('Someone else changed this row. The latest values are shown.');
          } else if (error instanceof ApiError && error.reason === CYCLE_NOT_EDITABLE) {
            setMessage('This cycle is no longer open for review changes.');
          } else if (error instanceof ApiError && error.errors.length === 0) {
            setMessage(error.message);
          }
        },
      },
    );
  };

  const history = (thread.data?.items ?? []).filter((item) => item.cycle_id !== cycleId);

  return (
    <Modal
      title="Review attributes"
      subtitle={`${current.client.name} · cycle ${cycleCode}`}
      onClose={onClose}
      onSubmit={submit}
      busy={save.isPending}
      submitLabel={readOnly ? 'Close' : 'Save'}
      submitIcon={readOnly ? undefined : <Check aria-hidden="true" />}
      layout="plain"
      message={message}
    >
      {changedElsewhere && (
        <div className="alert alert-info rv-live-banner" role="status">
          <Info aria-hidden="true" />
          <div>
            <div className="a-title">Updated by someone else while you were editing</div>
            <div className="a-body">Your typed values are kept. Saving will show the latest values first.</div>
          </div>
        </div>
      )}
      <div className="grid grid-3 mb-4">
        <div className="field">
          <label id="rv-risk-label">Risk (H/M/L) to COM</label>
          <div className="rv-segmented" role="radiogroup" aria-labelledby="rv-risk-label">
            {RISKS.map((code) => (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={form.risk === code}
                className={`rv-seg rv-seg-${code}${form.risk === code ? ' is-on' : ''}`}
                disabled={!canAttributes}
                onClick={() => setForm({ ...form, risk: form.risk === code ? '' : code })}
              >
                {code}
              </button>
            ))}
          </div>
          {fieldError('risk_rating_code') && <span className="field-error">{fieldError('risk_rating_code')}</span>}
        </div>
        <div className="field">
          <label htmlFor="rv-upside">Upside ({current.current_com.currency_code})</label>
          <input
            id="rv-upside"
            inputMode="decimal"
            value={form.upside}
            disabled={!canAttributes}
            onChange={(e) => setForm({ ...form, upside: e.target.value })}
          />
          {fieldError('upside') && <span className="field-error">{fieldError('upside')}</span>}
        </div>
        <div className="field">
          <label htmlFor="rv-downside">Downside ({current.current_com.currency_code})</label>
          <input
            id="rv-downside"
            inputMode="decimal"
            value={form.downside}
            disabled={!canAttributes}
            onChange={(e) => setForm({ ...form, downside: e.target.value })}
          />
          {fieldError('downside') && <span className="field-error">{fieldError('downside')}</span>}
        </div>
      </div>
      <div className="field">
        <label htmlFor="rv-comment">Comments {closingMonthName}</label>
        <textarea
          id="rv-comment"
          rows={4}
          maxLength={4000}
          value={form.comment}
          disabled={!canComment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
        />
        {fieldError('comment') && <span className="field-error">{fieldError('comment')}</span>}
        <span className="hint">Comments carry month over month, one per client per cycle.</span>
      </div>
      <div className="rv-history">
        <h4 className="rv-history-title">Earlier comments</h4>
        <CommentThread items={history} loading={thread.isLoading} />
      </div>
      {readOnly && <p className="hint">You can read these attributes but not change them.</p>}
    </Modal>
  );
}
